import { I18nError } from "@/i18n/messages.js";
const DEFAULT_MAX_FILE_BYTES = 15 * 1024 * 1024;
const SAMPLE_SIZE = 10;
const GENERATED_SYMBOLS = ["1", "2", "3", "4", "5", "6"];
const FALLBACK_PALETTE = ["C", "B", "G", "Y", "R", "W"];

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

function colorDistance(a, b) {
  const dr = a.r - b.r;
  const dg = a.g - b.g;
  const db = a.b - b.b;
  return Math.sqrt(dr * dr + dg * dg + db * db);
}

function luminance(color) {
  return 0.2126 * color.r + 0.7152 * color.g + 0.0722 * color.b;
}

function saturation(color) {
  const max = Math.max(color.r, color.g, color.b);
  const min = Math.min(color.r, color.g, color.b);
  if (max === 0) return 0;
  return (max - min) / max;
}

function rgbToHex(color) {
  const toHex = value => clamp(Math.round(value), 0, 255).toString(16).padStart(2, "0");
  return `#${toHex(color.r)}${toHex(color.g)}${toHex(color.b)}`;
}

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(new I18nError("imageReadFailed"));
    reader.readAsDataURL(file);
  });
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new I18nError("imageReadFailed"));
    image.src = src;
  });
}

function dataUrlToBlob(dataUrl) {
  const [header, encoded = ""] = String(dataUrl || "").split(",", 2);
  const mimeType = /^data:([^;]+)/i.exec(header)?.[1] || "image/webp";
  const binary = atob(encoded);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return new Blob([bytes], { type: mimeType });
}

function createStoredSourceImage(image, maxDimension = 720) {
  const sourceWidth = image.naturalWidth || image.width;
  const sourceHeight = image.naturalHeight || image.height;
  const scale = Math.min(1, maxDimension / Math.max(sourceWidth, sourceHeight));
  const width = Math.max(1, Math.round(sourceWidth * scale));
  const height = Math.max(1, Math.round(sourceHeight * scale));
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d", { alpha: true });
  if (!ctx) return image.src;
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(image, 0, 0, width, height);
  // 当前自定义地图仍保存在 localStorage，压缩后可显著降低手机照片导致容量溢出的概率。
  return canvas.toDataURL("image/webp", 0.76);
}

function averageRegion(data, width, x0, y0, regionWidth, regionHeight) {
  let r = 0;
  let g = 0;
  let b = 0;
  let alphaSum = 0;
  let count = 0;
  for (let y = y0; y < y0 + regionHeight; y += 1) {
    for (let x = x0; x < x0 + regionWidth; x += 1) {
      const offset = (y * width + x) * 4;
      const alpha = data[offset + 3] / 255;
      if (alpha <= 0.02) continue;
      r += data[offset] * alpha;
      g += data[offset + 1] * alpha;
      b += data[offset + 2] * alpha;
      alphaSum += alpha;
      count += 1;
    }
  }
  if (!count || alphaSum <= 0.02) return { r: 0, g: 0, b: 0, a: 0 };
  return { r: r / alphaSum, g: g / alphaSum, b: b / alphaSum, a: clamp(alphaSum / count, 0, 1) };
}

function estimateBackground(samples) {
  const valid = samples.filter(sample => sample.a > 0.65);
  if (!valid.length) return null;
  return {
    r: valid.reduce((sum, sample) => sum + sample.r, 0) / valid.length,
    g: valid.reduce((sum, sample) => sum + sample.g, 0) / valid.length,
    b: valid.reduce((sum, sample) => sum + sample.b, 0) / valid.length
  };
}

function shouldDropAsBackground(sample, background) {
  if (sample.a < 0.18) return true;

  const light = luminance(sample);
  const sat = saturation(sample);
  // 不做 AI 抠图，只去掉透明区域、近纯白背景，以及与四周背景非常接近的区域。
  if (light > 247 && sat < 0.06) return true;

  if (!background) return false;
  const bgLight = luminance(background);
  const bgSat = saturation(background);
  const distance = colorDistance(sample, background);
  const borderLooksLikeBackground = bgSat < 0.16 || bgLight > 225 || bgLight < 28;
  return borderLooksLikeBackground && distance < 30;
}

function percentile(values, ratio) {
  if (!values.length) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  return sorted[Math.min(sorted.length - 1, Math.max(0, Math.floor((sorted.length - 1) * ratio)))];
}

function extractForegroundCells(sampleGrid, width, height, background) {
  const borderSamples = [];
  for (let x = 0; x < width; x += 1) {
    borderSamples.push(sampleGrid[0][x], sampleGrid[height - 1][x]);
  }
  for (let y = 1; y < height - 1; y += 1) {
    borderSamples.push(sampleGrid[y][0], sampleGrid[y][width - 1]);
  }
  const borderDistances = background
    ? borderSamples.filter(sample => sample.a > 0.18).map(sample => colorDistance(sample, background))
    : [];
  const backgroundThreshold = clamp(percentile(borderDistances, 0.72) * 1.45 + 12, 24, 68);
  const isBackgroundLike = sample => {
    if (sample.a < 0.18) return true;
    const light = luminance(sample);
    const sat = saturation(sample);
    if (light > 247 && sat < 0.07) return true;
    return Boolean(background && colorDistance(sample, background) <= backgroundThreshold);
  };

  // Flood only from the outer edge, so similarly colored details enclosed by
  // the subject remain part of the generated brick silhouette.
  const flooded = Array.from({ length: height }, () => Array(width).fill(false));
  const queue = [];
  const enqueue = (x, y) => {
    if (x < 0 || x >= width || y < 0 || y >= height || flooded[y][x]) return;
    if (!isBackgroundLike(sampleGrid[y][x])) return;
    flooded[y][x] = true;
    queue.push([x, y]);
  };
  for (let x = 0; x < width; x += 1) {
    enqueue(x, 0);
    enqueue(x, height - 1);
  }
  for (let y = 0; y < height; y += 1) {
    enqueue(0, y);
    enqueue(width - 1, y);
  }
  for (let index = 0; index < queue.length; index += 1) {
    const [x, y] = queue[index];
    enqueue(x - 1, y);
    enqueue(x + 1, y);
    enqueue(x, y - 1);
    enqueue(x, y + 1);
  }

  let mask = Array.from({ length: height }, (_, y) =>
    Array.from({ length: width }, (_, x) => !flooded[y][x] && sampleGrid[y][x].a >= 0.18)
  );
  const initialCount = mask.reduce((sum, row) => sum + row.filter(Boolean).length, 0);

  // If nearly the complete rectangle survived, the photo has a varied
  // background. Keep only cells with a stronger difference from the edge.
  if (background && initialCount > width * height * 0.84) {
    const strictThreshold = Math.min(105, backgroundThreshold * 1.45);
    mask = mask.map((row, y) => row.map((kept, x) =>
      kept && colorDistance(sampleGrid[y][x], background) > strictThreshold
    ));
  }

  // Remove one-cell compression noise while retaining thin two-cell outlines.
  const cleaned = mask.map((row, y) => row.map((kept, x) => {
    if (!kept) return false;
    let neighbors = 0;
    for (let dy = -1; dy <= 1; dy += 1) {
      for (let dx = -1; dx <= 1; dx += 1) {
        if ((dx || dy) && mask[y + dy]?.[x + dx]) neighbors += 1;
      }
    }
    return neighbors >= 2;
  }));

  const cells = [];
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      if (cleaned[y][x]) cells.push({ x, y: y + 1, color: sampleGrid[y][x] });
    }
  }
  return cells;
}

function addContextualBarriers(rows, cells, width, height) {
  if (!cells.length) return;
  const minX = Math.min(...cells.map(cell => cell.x));
  const maxX = Math.max(...cells.map(cell => cell.x));
  const minY = Math.min(...cells.map(cell => cell.y));
  const maxY = Math.max(...cells.map(cell => cell.y));
  const occupied = new Set(cells.map(cell => `${cell.x}:${cell.y}`));
  const convertToBarrier = (x, y) => {
    if (x >= 0 && x < width && y > 0 && y < height && rows[y][x] !== " ") rows[y][x] = "#";
  };

  // Convert the subject's own lower-facing contour into short grey rebound
  // edges. No new pixels are added outside the extracted photograph shape.
  const subjectHeight = Math.max(1, maxY - minY + 1);
  const lowerContour = cells.filter(cell =>
    cell.y >= minY + subjectHeight * 0.42
    && !occupied.has(`${cell.x}:${cell.y + 1}`)
  );
  for (const cell of lowerContour) {
    // Keep regular openings so the grey contour never seals the full subject.
    if ((cell.x - minX) % 7 !== 3 && (cell.x - minX) % 7 !== 4) {
      convertToBarrier(cell.x, cell.y);
    }
  }

  // Wide subjects gain one or two internal support bands by converting only
  // existing colored cells near those rows. The middle stays open.
  const subjectWidth = Math.max(1, maxX - minX + 1);
  if (subjectWidth >= 18 && subjectHeight >= 9) {
    const bandRows = [
      minY + Math.round(subjectHeight * 0.56),
      ...(subjectHeight >= 18 ? [minY + Math.round(subjectHeight * 0.76)] : [])
    ];
    for (const bandY of bandRows) {
      for (let x = minX; x <= maxX; x += 1) {
        const relative = (x - minX) / subjectWidth;
        const inSupportSegment = (relative >= 0.08 && relative <= 0.34)
          || (relative >= 0.66 && relative <= 0.92);
        if (inSupportSegment && occupied.has(`${x}:${bandY}`)) convertToBarrier(x, bandY);
      }
    }
  }
}

function distinctSeedCandidates(colors) {
  const buckets = new Map();
  for (const color of colors) {
    const key = `${Math.round(color.r / 24)}-${Math.round(color.g / 24)}-${Math.round(color.b / 24)}`;
    if (!buckets.has(key)) buckets.set(key, color);
  }
  return [...buckets.values()];
}

function chooseInitialCenters(colors, count) {
  const candidates = distinctSeedCandidates(colors);
  const source = candidates.length >= count ? candidates : colors;
  const centers = [source[Math.floor(source.length / 2)]];
  while (centers.length < count) {
    let best = source[0];
    let bestDistance = -1;
    for (const color of source) {
      const nearest = Math.min(...centers.map(center => colorDistance(color, center)));
      if (nearest > bestDistance) {
        bestDistance = nearest;
        best = color;
      }
    }
    centers.push({ ...best });
  }
  return centers.map(center => ({ ...center }));
}

function quantizeColors(colors, maxColors = 6) {
  const uniqueCount = distinctSeedCandidates(colors).length;
  const count = Math.max(1, Math.min(maxColors, uniqueCount, colors.length));
  let centers = chooseInitialCenters(colors, count);

  for (let iteration = 0; iteration < 8; iteration += 1) {
    const sums = Array.from({ length: centers.length }, () => ({ r: 0, g: 0, b: 0, count: 0 }));
    for (const color of colors) {
      const index = nearestCenterIndex(color, centers);
      sums[index].r += color.r;
      sums[index].g += color.g;
      sums[index].b += color.b;
      sums[index].count += 1;
    }
    centers = centers.map((center, index) => {
      const sum = sums[index];
      if (!sum.count) return center;
      return { r: sum.r / sum.count, g: sum.g / sum.count, b: sum.b / sum.count };
    });
  }

  return centers;
}

function nearestCenterIndex(color, centers) {
  let bestIndex = 0;
  let bestDistance = Number.POSITIVE_INFINITY;
  centers.forEach((center, index) => {
    const distance = colorDistance(color, center);
    if (distance < bestDistance) {
      bestDistance = distance;
      bestIndex = index;
    }
  });
  return bestIndex;
}

export async function generateBrickMapFromImageFile(file, {
  width = 51,
  height = 31,
  maxColors = 6,
  maxFileBytes = DEFAULT_MAX_FILE_BYTES
} = {}) {
  if (!(file instanceof File) || !String(file.type || "").startsWith("image/")) {
    throw new I18nError("imageFileRequired");
  }
  if (file.size > maxFileBytes) {
    throw new I18nError("imageTooLarge");
  }

  const originalDataUrl = await readFileAsDataUrl(file);
  const image = await loadImage(originalDataUrl);
  const sourceImageDataUrl = createStoredSourceImage(image);

  const drawableHeight = Math.max(1, height - 1); // 第 0 行继续保留安全边界砖。
  const renderWidth = width * SAMPLE_SIZE;
  const renderHeight = drawableHeight * SAMPLE_SIZE;
  const canvas = document.createElement("canvas");
  canvas.width = renderWidth;
  canvas.height = renderHeight;
  const ctx = canvas.getContext("2d", { alpha: true, willReadFrequently: true });
  if (!ctx) throw new I18nError("imageUnsupported");
  ctx.clearRect(0, 0, renderWidth, renderHeight);
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";

  const imageWidth = image.naturalWidth || image.width;
  const imageHeight = image.naturalHeight || image.height;
  // contain：完整保留用户上传图片，不裁掉图片边缘。
  const scale = Math.min(renderWidth / imageWidth, renderHeight / imageHeight);
  const drawWidth = imageWidth * scale;
  const drawHeight = imageHeight * scale;
  const drawX = (renderWidth - drawWidth) / 2;
  const drawY = (renderHeight - drawHeight) / 2;
  ctx.drawImage(image, drawX, drawY, drawWidth, drawHeight);

  const imageData = ctx.getImageData(0, 0, renderWidth, renderHeight);
  const cornerSize = SAMPLE_SIZE * 2;
  const background = estimateBackground([
    averageRegion(imageData.data, renderWidth, 0, 0, cornerSize, cornerSize),
    averageRegion(imageData.data, renderWidth, renderWidth - cornerSize, 0, cornerSize, cornerSize),
    averageRegion(imageData.data, renderWidth, 0, renderHeight - cornerSize, cornerSize, cornerSize),
    averageRegion(imageData.data, renderWidth, renderWidth - cornerSize, renderHeight - cornerSize, cornerSize, cornerSize)
  ]);

  const sampleGrid = [];
  for (let y = 0; y < drawableHeight; y += 1) {
    const sampleRow = [];
    for (let x = 0; x < width; x += 1) {
      sampleRow.push(averageRegion(
        imageData.data,
        renderWidth,
        x * SAMPLE_SIZE,
        y * SAMPLE_SIZE,
        SAMPLE_SIZE,
        SAMPLE_SIZE
      ));
    }
    sampleGrid.push(sampleRow);
  }

  const sampledCells = extractForegroundCells(sampleGrid, width, drawableHeight, background);

  if (!sampledCells.length) {
    throw new I18nError("imageNoRegion");
  }

  // 传统 K-means 颜色量化：不是 AI，也不需要联网。将图片主色压缩到最多 6 种砖块颜色。
  const centers = quantizeColors(sampledCells.map(cell => cell.color), Math.min(6, maxColors));
  const symbols = GENERATED_SYMBOLS.slice(0, centers.length);
  const customColors = Object.fromEntries(
    centers.map((center, index) => [symbols[index], rgbToHex(center)])
  );

  const rows = Array.from({ length: height }, () => Array.from({ length: width }, () => " "));
  for (let x = 0; x < width; x += 1) rows[0][x] = "#";
  for (const cell of sampledCells) {
    rows[cell.y][cell.x] = symbols[nearestCenterIndex(cell.color, centers)];
  }
  addContextualBarriers(rows, sampledCells, width, height);

  const paletteSlots = [...symbols];
  for (const symbol of FALLBACK_PALETTE) {
    if (paletteSlots.length >= 6) break;
    paletteSlots.push(symbol);
  }
  paletteSlots.push("#", " ");

  return {
    rows,
    customColors,
    paletteSlots,
    sourceImageDataUrl,
    sourceImageBlob: dataUrlToBlob(sourceImageDataUrl),
    breakableCount: sampledCells.length,
    originalWidth: imageWidth,
    originalHeight: imageHeight
  };
}
